import Collection from "../../data-structures/collection"
import Goal from "./goal"
import ApiGoal from "../../gd/api/goal"
import ArrayUtils from "../../gd/utils/array"
import {GoalType, RenderDisplay} from "./const"
import goalFactory from "./factory"
import GoalTask from "./item/goal-task"
import GoalProject from "./item/goal-project"
import GoalEvent from "./item/goal-event"
import asyncSessionLoader from "../../gd/data/async-session-loader"

export type GoalApiStatus = 'all' | 'archived' | 'active'

const debug = false
// const debug = "GoalsCollection"

export default class GoalsCollection extends Collection<Goal> {

    public filterCallback?  : {(collection:GoalsCollection):void}

    protected byTaskId      : {[key:ID]:GoalTask}       = {}
    protected byProjectId   : {[key:ID]:GoalProject}    = {}
    protected byEventId     : {[key:ID]:GoalEvent}      = {}

    constructor() {
        super([])
    }

    static initFromJson(data: any): GoalsCollection {
        const models: Goal[] = []

        _.map(data, (record: JSONObject) => {
            const goal = goalFactory(record)
            switch (goal.type) {
                case GoalType.TASK      : if ((goal as GoalTask).noEntity) return; break
                case GoalType.PROJECT   : if ((goal as GoalProject).noEntity) return; break
                case GoalType.EVENT     : if ((goal as GoalEvent).noEntity) return; break
            }
            models.push(goal)
        })

        // is required because byTaskId, byProjectId and byEventId are reset if populated through constructor
        const collection = new GoalsCollection()
        collection.reset(models)

        return collection
    }

    static async initFromApi(options: {
        projectId?  : ID,
        goalStatus? : GoalApiStatus
    } = {}): Promise<GoalsCollection> {
        let response
        if (options.projectId) {
            response = await ApiGoal.getProjectGoals(options.projectId, options.goalStatus).promise()
        } else {
            response = await ApiGoal.getAllGoals(options.goalStatus).promise()
        }

        await asyncSessionLoader(GoalsCollection.findMissingData(response))

        return GoalsCollection.initFromJson(response)
    }

    static findMissingData(response: any[]) {
        debug && console.log(debug, 'findMissingData', response)

        let allUsers: ID[]      = []
        let allTaskTypes: ID[]  = []
        let allStatuses: ID[]   = []
        let allProjects: ID[]   = []

        _.map(response, goal=>{
            allUsers = allUsers.concat(_.map(goal.users, user=>user.userId))
            if (goal.ownerUsersIds)     allUsers = allUsers.concat(goal.ownerUsersIds)
            if (goal.assigneeUserIds)   allUsers = allUsers.concat(goal.assigneeUserIds)

            allProjects.push(goal.projectId)

            if (goal.task) {
                allUsers = allUsers.concat([goal.task.actionRequiredUserId, goal.task.assignedToUserId])
                allTaskTypes.push(goal.task.taskTypeId)
                allStatuses.push(goal.task.taskStatusId)
                allProjects.push(goal.task.projectId)
            }
            if (goal.project) {
                allStatuses.push(goal.project.statusId)
                allProjects.push(goal.project.id)
            }
            if (goal.event) {
                allUsers.push(goal.event.assignedToUserId)
            }
        })

        return gd.session.findMissing({
            users       : _(allUsers).uniq().compact().value(),
            taskTypes   : _(allTaskTypes).uniq().compact().value(),
            statuses    : _(allStatuses).uniq().compact().value(),
            projects    : _(allProjects).uniq().compact().value(),
        })
    }

    get rootGoals(): GoalsCollection {
        const collection = new GoalsCollection()
        collection.reset(_.filter(this.models, (goal) => !goal.parentGoal))
        return collection
    }

    get hasVisibleGoals(): boolean {
        return this.visibleLength > 0;
    }

    get visibleLength() {
        return _.filter(this.models, goal=>goal.render.display === RenderDisplay.VISIBLE).length
    }

    getByTaskId(taskId: ID): GoalTask {
        return this.byTaskId[taskId]
    }

    getByProjectId(projectId: ID): GoalProject {
        return this.byProjectId[projectId]
    }

    getByEventId(eventId: ID): GoalEvent {
        return this.byEventId[eventId]
    }

    addFromJson(data: any) {
        this.add(goalFactory(data))
    }

    reset(models?:Goal[]) {
        this.byTaskId       = {}
        this.byProjectId    = {}
        this.byEventId      = {}

        super.reset(models)
    }

    add(model: Goal) {
        // add duplicate (same id) check
        const alreadyAddedModel = this.get(model.id)
        if (alreadyAddedModel) {
            alreadyAddedModel.applyDelta(model.toJSON())
            return
        }

        super.add(model)

        debug && console.log(debug, 'add', model)

        this._initParentRootGoal(model)

        switch (model.type) {
            case GoalType.TASK      : this.byTaskId[model.entityId!] = model as GoalTask; break
            case GoalType.PROJECT   : this.byProjectId[model.entityId!] = model as GoalProject; break
            case GoalType.EVENT     : this.byEventId[model.entityId!] = model as GoalEvent; break
        }
    }

    remove(id: ID): Goal[] {
        const removedModels = super.remove(id)

        debug && console.log(debug, 'remove', id, removedModels)

        const model = removedModels[0]
        if (model?.parentGoalId) {
            const parentGoal = this.get(model.parentGoalId)

            if (parentGoal) {
                model.parentGoal = parentGoal

                if (!parentGoal.subGoals) {
                    parentGoal.subGoals = new GoalsCollection()
                }

                parentGoal.subGoals?.remove(id)
            }
        }

        // remove all subGoals of removed goal
        _.map(model.subGoals?.models, (subGoal: Goal) => {
            this.remove(subGoal.id)
        })

        switch (model.type) {
            case GoalType.TASK      : delete this.byTaskId[model.entityId!]; break
            case GoalType.PROJECT   : delete this.byProjectId[model.entityId!]; break
            case GoalType.EVENT     : delete this.byEventId[model.entityId!]; break
        }

        return removedModels
    }

    // should not be called on full collection (rootGoals or subGoals only)
    move(movedGoalId: ID, relativePosition: 'before' | 'after', relativeGoalId: ID) {
        const orderedIds    = _.map(this.models, model=>model.id)
        const oldIndex      = _.indexOf(orderedIds, movedGoalId)
        let newIndex        = _.indexOf(orderedIds, relativeGoalId)

        switch (relativePosition) {
            case 'before': if (oldIndex < newIndex) newIndex--; break; //move before target position
            case 'after': if (oldIndex > newIndex) newIndex++; break; //move after target position
        }

        debug && console.log(debug, "move", movedGoalId, relativeGoalId, relativePosition, oldIndex, newIndex)

        ArrayUtils.moveInArrayMutate(this.models, oldIndex, newIndex)

        const goalUpdates: any = {}

        _.map(this.models, (model: Goal, key:number)=>{
            const newSortPosition = key+1

            // collect goal updates if required
            if (model.sortPosition !== newSortPosition) {
                goalUpdates[model.id] = {
                    sortPosition: newSortPosition
                }
                model.sortPosition = newSortPosition
            }
        })

        ApiGoal.bulkUpdate({goalUpdates})

        debug && console.log(debug, 'move', orderedIds, _.map(this.models, model=>model.id))
    }

    _initParentRootGoal(goal: Goal) {
        if (goal.parentGoalId) {
            const parentGoal = this.get(goal.parentGoalId)

            if (parentGoal) {
                goal.parentGoal = parentGoal
                parentGoal.subGoals.add(goal)
            }
        }

        if (goal.rootGoalId) {
            const rootGoal = this.get(goal.rootGoalId)

            if (rootGoal) {
                goal.rootGoal = rootGoal
            }
        }
    }

    initParentsAndChildren(): void {
        // do not forget to reset (it) as we run over and over
        this.models.map((goal) => {
            goal.parentGoal = undefined
            // goal.rootGoal = undefined
            goal.subGoals.reset()
        })

        this.models.map((goal) => {
            this._initParentRootGoal(goal)
        })

        // remove all goals that do not have root goal in collection
        // this may occur because of goal.noEntity check on creation
        _.remove(this.models, goal=>!goal.rootGoal)
    }

    sort() {
        this.models = _.orderBy(this.models, [
            model=>model.sortPosition,
            model=>model.momentCreated?.format('x'),
            model=>model.name
        ], ['asc', 'asc'])
    }

    calculateTotalProgress(): number {
        return _.reduce(this.models, (sum, goal) => {
            goal.calculateTotalProgress()
            return sum + goal.totalProgress
        }, 0) / this.models.length
    }

    update() {
        this.initParentsAndChildren()
        this.calculateTotalProgress()
        this.filterCallback && this.filterCallback(this)
    }

    toJSON(): any {
        return _.map(this.models, (model) => model.toJSON())
    }
}
